import {FC, useEffect, useRef, useState} from 'react'
import {KTSVG, toAbsoluteUrl} from '../../../_metronic/helpers'
import {BiographyTabs} from './components/BiographyTabs'
import {shallowEqual, useSelector} from 'react-redux'
import {RootState} from '../../../setup'
import {UserModel} from '../../modules/auth/models/UserModel'
import {useUserProfileContext} from '../../context/UserProfileContext'
import {hasPermission, hasRole, hasTeamModuleAction} from '../../utils/permissions'
import {Permission, UserRole} from '../../constants/roles'
import {useHistory} from 'react-router-dom'
import {updateProfileImg} from '../../services/profileService'
import SVG from 'react-inlinesvg'
import {showErrorAlert} from '../../helpers/alert'
import { socialOptions } from '../profile/components/fields/utils'
import { defaultProfileImage } from '../../utils/constants'
import GenericTooltip from '../../hooks/utils/GenericTooltip'
import { ProfileMemberBadges } from '../profileMembers/components/ProfileMemberBadges'
import { useConfirmAction } from '../../hooks/utils/useConfirmAction'

const BiographyPage: FC = () => {
  const {data, refetch, searchParams, setSearchParams, loading} = useUserProfileContext()
  const user = useSelector<RootState>(({auth}) => auth.user, shallowEqual) as UserModel
  const isTeamMember = hasRole(user, UserRole.TEAM)
  const canEditImage = isTeamMember
    ? hasTeamModuleAction(user, 'profile', 'canEdit')
    : hasPermission(user, Permission.EDIT_PROFILE_IMAGE)
  const fullName = `${data?.firstName} ${data?.lastName}`
  const [isMember, setIsMember] = useState<boolean>(false)
  const [isExpert, setIsExpert] = useState<boolean>(false)
  const [profileImage, setProfileImage] = useState<string>(toAbsoluteUrl(''))
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const navigate = useHistory()
  const [module, setModule] = useState<string>('')
  const [isImageLoading, setIsImageLoading] = useState<boolean>(true)

  const searchableUser = isTeamMember ? false : (user.id == data?.user) ? false : true;// !!searchParams.userSelected
  let confirm = useConfirmAction()
  useEffect(() => {
    if (!isTeamMember && Number(data?.user ?? 0) != Number(user.id)){
      setSearchParams({ userSelected: user.id });
    }
    if (data?.subscription) setIsMember(true)
    if (data?.expertise && data.expertise.length > 0) setIsExpert(true)
  }, [data])

  useEffect(() => {
    if (
      data?.profilePicture &&
      typeof data.profilePicture === 'string' &&
      data.profilePicture.trim() !== ''
    ) {
      setIsImageLoading(true)
      setProfileImage(`${data.profilePicture}?t=${Date.now()}`)
    }
  }, [data?.profilePicture])

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleImageLoad = () => {
    setIsImageLoading(false)
  }

  const handleImageError = (e:any) => {
    setIsImageLoading(false)
    e.currentTarget.src = defaultProfileImage;
  }

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    let isChangeImage = await confirm({
      title: 'Change profile photo',
      message: 'Are you sure you want to change your profile photo?',
      btnTextAction: 'Yes, change it',
      btnTextDecline: 'No, keep current photo',
    })
    if(!isChangeImage) {
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      // opcional, usando el evento
      event.target.value = ''
      return
    }

    const file = event.target.files?.[0]
    if (!file) return

    const previousImage = profileImage

    const reader = new FileReader()
    reader.onload = (e) => {
      const imageUrl = e.target?.result as string
      setProfileImage(imageUrl)
    }
    reader.readAsDataURL(file)

    setIsUploading(true)

    try {
      await updateProfileImg(file)
      await refetch()
    } catch (error: any) {
      const statusCode = error?.response?.status || 500
      showErrorAlert({
        title: 'Unable to update profile photo',
        message: "We couldn't save your changes. Please review your information and try again.",
        errorCode: `PROFILE_IMG${statusCode}`,
      })

      setProfileImage(previousImage)
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      // opcional, usando el evento
      event.target.value = ''
    }
  }

  const handleModule = async (module: string) => {
    setModule(module)
  }

  return (
    <>
      <div className='biography'>
        {/* LEFT */}
        <div className='biography__left'>
          {/* Badges */}
          <div className='biography__badges'>
            {isMember ? (
              // Armando 2026-09-10: chip Expert se dispara con partnerStatus === 'active'
              // (mismo criterio que perfil publico PR-I-85). Antes iba hardcoded a false
              // y members con expertise activa/vigente no veian el chip en su propio perfil.
              <ProfileMemberBadges hasExpertise={data?.partnerStatus === 'active'} subscription={data?.subscription} isFoundingMember={data?.isFoundingMember} />
            ) : null}
          </div>

          {/* Info */}
          <div className='biography__info'>
            <div className='biography__info-name'> {`${data?.aliasTitle??''} ${fullName}`}</div>
            <div className='biography__divider'></div>
            <div className='biography__info-desc'>
              <div className='biography__info-desc'>
                {data?.introductionHeadline}
              </div>
            </div>

            <div className='biography__info-location'>
              <span>
                <KTSVG path='/media/svg/nobilis/location_mark.svg' />
                {data?.city || data?.postalAddress}
              </span>
            </div>
          </div>

          {/* Languages */}
          <div className='biography__languages'>
            <div className='biography__languages-title'>Language Spoken</div>
            <div className='biography__languages-list'>
              {(data?.languages?.length > 0) &&
                data.languages.map((item, index) => {
                  return (
                    <div key={index} className='lang'>
                      {item}
                    </div>
                  )
                }
              )}
              {(data?.languages?.length == 0) && (<div className='biography__languages-list'>Not provided</div>)}
            </div>
          </div>
          <div className='biography__languages'>
            <div className='biography__languages-title'>My Introductions</div>
            <div className='biography__languages-list'>
              {data?.introduction &&
                data.introduction.map((item, index) => {
                  return (
                    <div key={index} className='lang'>
                      {item}
                    </div>
                  )
                })}
            </div>
          </div>

          {/* Actions */}
          <div className='biography__actions'>
            <div className='biography__actions-socials'>
              {data?.socialMediaProfiles?.map((profile, index) => {
                const option = socialOptions.find((opt) => opt.name.toLowerCase() === profile.platformName.toLowerCase());
                if (!option) return null

                return (<a key={`link-${index}`} href={profile.profileUrl} target='_blank'><SVG key={index} src={toAbsoluteUrl(option.icon2)} /></a>);
              })}
            </div>
          </div>
        </div>
        <div className='biography__right'>
          <div className='biography__watermark'>
            <img src={toAbsoluteUrl('/media/svg/nobilis/mark_bg.svg')} alt='watermark' />
          </div>

          <div className='bg-block'></div>

          <input
            ref={fileInputRef}
            type='file'
            accept='image/*'
            style={{display: 'none'}}
            onChange={handleImageChange}
          />

          {isImageLoading && <div className='biography__image-skeleton' />}
          <div className="quote-profile">
            {canEditImage && !searchableUser && (
              <>
                <a
                  className='biography__edit-btn cursor-pointer my-profile'
                  onClick={() => fileInputRef.current?.click()}
                >
                  <KTSVG path='/media/svg/nobilis/edit-img.svg' />
                </a>
              </>
            )}
            <img
              src={profileImage}
              alt='profile'
              className={isImageLoading ? 'biography__image--hidden' : ''}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
            <div className='quote'>
              <span>{data?.guidingPrinciple}</span>
            </div>
          </div>


          {canEditImage && !searchableUser && (
            <div className='biography__edit-container'>
              <button
                className='biography__edit-link'
                onClick={() => navigate.push('/admin/overview/profile')}
                type='button'
              >
                <span>EDIT</span>
                <KTSVG path='/media/svg/nobilis/vector1.svg' />
              </button>
            </div>
          )}
        </div>
      </div>

      <BiographyTabs onTabChange={handleModule} searchableUser={searchableUser} />
    </>
  )
}

export {BiographyPage}
